import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Trash2, Edit, Plus, Clock, Search, Loader2, ChevronDown, ChevronRight, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatEventDateRange } from "@/lib/events";

interface Event {
  id: number;
  title: string;
  event_date?: string;
  event_date_from?: string | null;
  event_date_to?: string | null;
}

interface AgendaItem {
  id: number;
  event_id?: number | null;
  day: string;
  date: string;
  time_range: string;
  title: string;
  description: string;
  sort_order: number;
}

interface AgendaFormData {
  day: string;
  date: string;
  start_time: string;
  end_time: string;
  title: string;
  description: string;
}

const DEFAULT_FORM: AgendaFormData = {
  day: "Day 1",
  date: "",
  start_time: "",
  end_time: "",
  title: "",
  description: "",
};

export function AgendaManager() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<AgendaItem | null>(null);
  const [newItem, setNewItem] = useState<AgendaFormData>(DEFAULT_FORM);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  // Track which day sections are open; default all open
  const [openDays, setOpenDays] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadAgenda(selectedEventId);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const response = await api.get<Event[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
        if (response.data.length > 0) {
          setSelectedEventId(response.data[0].id);
        }
      }
    } catch {
      toast.error("Failed to load events");
    }
  };

  const loadAgenda = async (eventId: number) => {
    try {
      const response = await api.get<AgendaItem[]>(`/api/agenda/event/${eventId}`);
      if (response.success && response.data) {
        setAgenda(response.data);
        // Open all day sections by default
        const days = new Set(response.data.map((item) => item.day || "Day 1"));
        setOpenDays(days);
      }
    } catch {
      toast.error("Failed to load agenda");
    }
  };

  const handleAdd = async () => {
    if (!newItem.start_time || !newItem.end_time || !newItem.title || !newItem.day) {
      toast.error("Please fill in day, start time, end time and title");
      return;
    }

    const time_range = `${newItem.start_time} - ${newItem.end_time}`;
    const payload = {
      event_id: selectedEventId ?? undefined,
      day: newItem.day,
      date: newItem.date,
      time_range,
      title: newItem.title,
      description: newItem.description,
    };

    setLoading(true);
    try {
      const response = await api.post<AgendaItem[]>("/api/agenda", payload);
      if (response.success && response.data) {
        setAgenda(response.data);
        // Ensure the new day section is open
        const days = new Set(response.data.map((item) => item.day || "Day 1"));
        setOpenDays(days);
        setNewItem(DEFAULT_FORM);
        setIsDialogOpen(false);
        toast.success("Agenda item added successfully");
      }
    } catch {
      toast.error("Failed to add agenda item");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    setLoading(true);
    try {
      const response = await api.put<AgendaItem>(`/api/agenda/${editingItem.id}`, editingItem);
      if (response.success && response.data) {
        setAgenda(agenda.map((item) => (item.id === editingItem.id ? response.data! : item)));
        setEditingItem(null);
        setIsDialogOpen(false);
        toast.success("Agenda item updated successfully");
      }
    } catch {
      toast.error("Failed to update agenda item");
    } finally {
      setLoading(false);
    }
  };

  const parseTimeRange = (time_range: string) => {
    const parts = time_range.split(" - ");
    return {
      start_time: parts[0] || "",
      end_time: parts[1] || "",
    };
  };

  const handleDelete = async () => {
    if (itemToDelete === null) return;

    setLoading(true);
    try {
      const response = await api.delete(`/api/agenda/${itemToDelete}`);
      if (response.success) {
        setAgenda(agenda.filter((item) => item.id !== itemToDelete));
        toast.success("Agenda item deleted successfully");
      }
    } catch {
      toast.error("Failed to delete agenda item");
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const confirmDelete = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const toggleDay = (day: string) => {
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  // Group agenda by day, preserving sort_order within each day
  const groupedAgenda = (() => {
    const filtered = agenda.filter(
      (item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.day || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const map = new Map<string, AgendaItem[]>();
    for (const item of filtered) {
      const day = (item.day as string | undefined) || "Day 1";
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(item);
    }

    // Sort items within each day by sort_order then time_range
    for (const [, items] of map) {
      items.sort((a, b) => {
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return a.time_range.localeCompare(b.time_range);
      });
    }

    // Return days sorted alphabetically (Day 1, Day 2, …)
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-primary mb-2">Agenda</h1>
          <p className="text-muted-foreground">Manage event schedule and agenda items</p>
        </div>
      </div>

      {/* Event selector */}
      <Card className="p-6">
        <div>
          <Label htmlFor="agenda-event">Select Event</Label>
          <Select
            value={selectedEventId?.toString() || ""}
            onValueChange={(value) => setSelectedEventId(parseInt(value))}
          >
            <SelectTrigger id="agenda-event" className="mt-1">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id.toString()}>
                  {event.title}
                  {(event.event_date_from || event.event_date) && ` (${formatEventDateRange(event)})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {selectedEventId && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search agenda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Add Item dialog */}
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => {
                      setEditingItem(null);
                      setNewItem(DEFAULT_FORM);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingItem ? "Edit Agenda Item" : "Add New Agenda Item"}
                    </DialogTitle>
                    <DialogDescription>
                      {editingItem
                        ? "Update the agenda item details below."
                        : "Add a new agenda item to the schedule."}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Day & Date row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="day">Day</Label>
                        <Input
                          id="day"
                          placeholder="e.g. Day 1"
                          value={editingItem ? editingItem.day || "" : newItem.day}
                          onChange={(e) =>
                            editingItem
                              ? setEditingItem({ ...editingItem, day: e.target.value })
                              : setNewItem({ ...newItem, day: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          type="date"
                          value={editingItem ? editingItem.date || "" : newItem.date}
                          onChange={(e) =>
                            editingItem
                              ? setEditingItem({ ...editingItem, date: e.target.value })
                              : setNewItem({ ...newItem, date: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    {/* Start & End time row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="start_time">Start Time</Label>
                        <Input
                          id="start_time"
                          type="time"
                          value={
                            editingItem
                              ? parseTimeRange(editingItem.time_range).start_time
                              : newItem.start_time
                          }
                          onChange={(e) =>
                            editingItem
                              ? setEditingItem({
                                  ...editingItem,
                                  time_range: `${e.target.value} - ${parseTimeRange(editingItem.time_range).end_time}`,
                                })
                              : setNewItem({ ...newItem, start_time: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="end_time">End Time</Label>
                        <Input
                          id="end_time"
                          type="time"
                          value={
                            editingItem
                              ? parseTimeRange(editingItem.time_range).end_time
                              : newItem.end_time
                          }
                          onChange={(e) =>
                            editingItem
                              ? setEditingItem({
                                  ...editingItem,
                                  time_range: `${parseTimeRange(editingItem.time_range).start_time} - ${e.target.value}`,
                                })
                              : setNewItem({ ...newItem, end_time: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <div>
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={editingItem ? editingItem.title : newItem.title}
                        onChange={(e) =>
                          editingItem
                            ? setEditingItem({ ...editingItem, title: e.target.value })
                            : setNewItem({ ...newItem, title: e.target.value })
                        }
                        placeholder="Enter agenda item title"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <RichTextEditor
                        content={editingItem ? editingItem.description : newItem.description}
                        onChange={(val) =>
                          editingItem
                            ? setEditingItem({ ...editingItem, description: val })
                            : setNewItem({ ...newItem, description: val })
                        }
                        placeholder="Enter description (optional)"
                        minHeight="100px"
                      />
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={editingItem ? handleUpdate : handleAdd}
                        disabled={loading}
                      >
                        {loading ? "Saving..." : editingItem ? "Update" : "Add"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Day-grouped accordion tables */}
          {groupedAgenda.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              No agenda items found.
            </Card>
          ) : (
            <div className="space-y-4">
              {groupedAgenda.map(([day, items]) => (
                <Collapsible
                  key={day}
                  open={openDays.has(day)}
                  onOpenChange={() => toggleDay(day)}
                >
                  <Card className="overflow-hidden">
                    {/* Day header — acts as the accordion trigger */}
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between px-6 py-4 bg-accent/10 hover:bg-accent/20 transition-colors text-left">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-accent" />
                          <span className="font-semibold text-primary text-lg">{day}</span>
                          {items[0]?.date && (
                            <Badge variant="outline" className="text-xs font-normal">
                              {items[0].date}
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {items.length} {items.length === 1 ? "item" : "items"}
                          </Badge>
                        </div>
                        {openDays.has(day) ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-36">Time</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">
                                <div className="flex items-center">
                                  <Clock className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
                                  {item.time_range}
                                </div>
                              </TableCell>
                              <TableCell>{item.title}</TableCell>
                              <TableCell className="text-muted-foreground max-w-xs truncate">
                                <div dangerouslySetInnerHTML={{ __html: item.description.replace(/<[^>]*>/g, " ").trim() }} />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end space-x-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setEditingItem(item);
                                      setIsDialogOpen(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => confirmDelete(item.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agenda Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this agenda item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
